import React from 'react';
import { AlertTriangle, Ban, Leaf, Sparkles } from 'lucide-react';

function titleCaseSlug(value) {
  return String(value || '')
    .replace(/^avoided_/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function cleanProfileLabel(title) {
  return String(title || '')
    .replace(/^My Diet:\s*/i, '')
    .replace(/\s+profile$/i, '')
    .replace(/^./, (char) => char.toUpperCase());
}

function extractAfterColon(title) {
  const parts = String(title || '').split(':');
  return parts.length > 1 ? parts.slice(1).join(':').trim() : '';
}

function groupMatches(matches) {
  const groups = [
    {
      id: 'allergen_watch',
      category: 'allergen_watch',
      title: 'Allergen Watch',
      values: [],
      detail: 'On your watch list.',
    },
    {
      id: 'avoided',
      category: 'avoided',
      title: 'Avoid',
      values: [],
      detail: 'On your no-thanks list.',
    },
    {
      id: 'my_diet',
      category: 'my_diet',
      title: 'My Diet',
      values: [],
      detail: 'Doesn’t match your profile — verdict is above.',
    },
  ];
  const groupByCategory = new Map(groups.map((group) => [group.category, group]));
  const other = { id: 'other', category: 'other', title: 'Food Profile', values: [] };

  matches.forEach((match) => {
    if (match.id === 'allergen_watch') {
      const value = extractAfterColon(match.title);
      if (value) groupByCategory.get('allergen_watch').values.push(value);
      if (match.detail) groupByCategory.get('allergen_watch').detail = match.detail;
      return;
    }

    if (String(match.id || '').startsWith('avoided_')) {
      groupByCategory.get('avoided').values.push(extractAfterColon(match.title) || titleCaseSlug(match.id.replace(/^avoided_/, '')));
      if (match.detail) groupByCategory.get('avoided').detail = match.detail;
      return;
    }

    if (['vegan', 'vegetarian', 'dairy_free', 'gluten_aware'].includes(match.id)) {
      groupByCategory.get('my_diet').values.push(cleanProfileLabel(match.title));
      if (match.detail) groupByCategory.get('my_diet').detail = match.detail;
      return;
    }

    if (match.title) other.values.push(match.title);
  });

  return [...groups, other].filter((group) => group.values.length > 0);
}

const GROUP_ICONS = {
  allergen_watch: AlertTriangle,
  avoided: Ban,
  my_diet: Leaf,
};

export function ForYouCard({ data, variant = 'default' }) {
  const matches = Array.isArray(data?.matches) ? data.matches : [];
  const groups = Array.isArray(data?.groups) && data.groups.length > 0
    ? data.groups
    : groupMatches(matches);
  if (groups.length === 0) return null;

  const isDesktopQuadrant = variant === 'desktop-quadrant';
  const rootClassName = isDesktopQuadrant
    ? 'w-full min-w-0 rounded-[1.75rem] border border-[#f0a24a] bg-[#fff3df] p-5 text-[#80400f]'
    : 'mb-4 rounded-[1.75rem] border border-[#f0a24a] bg-[#fff3df] p-5 text-[#80400f]';

  return (
    <section className={rootClassName}>
      <div>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center text-[#e2761b]">
            <Sparkles className="h-4.5 w-4.5" />
          </span>
          <div>
            <h3 className="text-xl font-semibold">Your plate</h3>
            <p className="mt-1 text-sm font-medium leading-6 text-[#80400f]">
              Here are the highlights, just for you — based on your Food Profile.
            </p>
          </div>
        </div>
      </div>
      <div>
        <div className="mt-4 divide-y divide-[#f0a24a]/35">
          {groups.map((group) => {
            const Icon = GROUP_ICONS[group.category] || Sparkles;
            return (
              <div key={group.id} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full border border-[#e2761b]/35 bg-[#fff3df]/70 text-[#e2761b]">
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-6 text-[#80400f]">
                    {group.title}: {group.values.join(', ')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs font-medium leading-5 text-[#80400f]">
          Your Food Profile adds personal context. It doesn't change the main VegAI result.
        </p>
      </div>
    </section>
  );
}
